import { LearnArticleLayout, H2, H3, P, UL, LI, Quote } from "./LearnArticleLayout";

export const HowToReadBirthChartPage = () => (
  <LearnArticleLayout
    slug="how-to-read-a-birth-chart"
    title="How to read a birth chart"
    subtitle="The anatomy of a chart, in plain language."
    seo_title="How to Read a Birth Chart: A Beginner's Guide"
    seo_desc="A clear beginner's guide to reading a birth chart. The wheel, the planets, the twelve houses and the aspects, in plain language. Start reading charts today."
    read_time="9 min read"
    updated="February 2026"
    related={[
      { slug: "rising-sign-vs-sun-sign-vs-moon-sign", to: "/learn/rising-sign-vs-sun-sign-vs-moon-sign", label: "Rising sign, sun sign, moon sign", hint: "The three placements every reading is really about." },
      { slug: "vedic-vs-western-astrology", to: "/learn/vedic-vs-western-astrology", label: "Vedic and Western astrology, side by side", hint: "Two traditions, one sky. Why they disagree." },
    ]}
  >
    <P>
      A birth chart looks like a wheel with a lot of symbols on it, and it puts most people off in the first ten seconds. That is a shame, because a chart is actually made of a small number of parts, and once you know what each part is doing, the whole picture makes sense.
    </P>
    <P>
      This is the beginner’s version. Not a diploma course. Just enough to look at your own chart and read it out loud to yourself without feeling lost.
    </P>

    <H2>What a birth chart actually is</H2>
    <P>
      A birth chart is a snapshot of the sky at the exact moment you were born, drawn from the point of view of the place you were born in. If you had looked up at that minute, in that spot, this is where every major planet would have been.
    </P>
    <P>
      Astrology reads that snapshot as a symbolic diagram of who you are. Which is either meaningful or is not, depending on your beliefs. Either way, the chart itself is astronomy. It is a map, not a prediction.
    </P>

    <H2>The four things every chart is made of</H2>
    <P>
      Look at any chart and you will find exactly four kinds of information stacked on top of each other:
    </P>
    <UL>
      <LI><strong>The signs</strong>: the twelve zodiac signs, arranged around the outer edge of the wheel.</LI>
      <LI><strong>The planets</strong>: the sun, moon, and the traditional planets, placed inside the wheel wherever they were in the sky at your birth.</LI>
      <LI><strong>The houses</strong>: twelve pie slices of the wheel, numbered one to twelve, each one covering a life area.</LI>
      <LI><strong>The aspects</strong>: the lines drawn between planets, showing how they are angled to each other.</LI>
    </UL>
    <P>
      That is the whole chart. Signs, planets, houses, aspects. Everything you have ever read about astrology sits somewhere inside those four categories.
    </P>

    <H2>The signs are the {'"how"'}</H2>
    <P>
      A sign is a flavour. It describes the mood or style with which something happens. Aries is direct and impatient. Taurus is slow and sensual. Gemini is quick and curious. And so on for the other nine.
    </P>
    <P>
      A sign on its own does not do anything. It just tells you the register a planet or a house is operating in. Think of the signs as adjectives.
    </P>

    <H2>The planets are the {'"what"'}</H2>
    <P>
      A planet is a force. It represents a category of experience that runs through your life.
    </P>
    <UL>
      <LI><strong>Sun</strong>: your core self, your drive, your ego structure.</LI>
      <LI><strong>Moon</strong>: your emotional life, your instincts, your relationship to home.</LI>
      <LI><strong>Mercury</strong>: how you think, learn, talk, write.</LI>
      <LI><strong>Venus</strong>: what you love, what you find beautiful, how you relate.</LI>
      <LI><strong>Mars</strong>: how you act, fight, want, take risks.</LI>
      <LI><strong>Jupiter</strong>: where you grow, expand, get lucky.</LI>
      <LI><strong>Saturn</strong>: where you struggle, master a discipline, meet real limits.</LI>
    </UL>
    <P>
      Modern astrology also uses Uranus, Neptune and Pluto for generational themes. The seven above are the daily workhorses.
    </P>
    <P>
      Every planet is in one sign in your chart. That combination gives you the classic sentence structure of astrology: “Venus in Libra” (how you love, in a diplomatic style), “Mars in Aries” (how you act, in a direct style), “Mercury in Virgo” (how you think, precisely).
    </P>

    <H2>The houses are the {'"where"'}</H2>
    <P>
      The twelve houses divide life into twelve rooms. Each house covers a slice of experience.
    </P>
    <UL>
      <LI><strong>1st house</strong>: self, body, appearance, first impressions.</LI>
      <LI><strong>2nd house</strong>: money, possessions, values.</LI>
      <LI><strong>3rd house</strong>: siblings, short trips, everyday communication.</LI>
      <LI><strong>4th house</strong>: home, family, roots, private life.</LI>
      <LI><strong>5th house</strong>: romance, creativity, children, play.</LI>
      <LI><strong>6th house</strong>: work, routine, health, service.</LI>
      <LI><strong>7th house</strong>: partnerships, marriage, close others.</LI>
      <LI><strong>8th house</strong>: shared resources, sex, death, transformation.</LI>
      <LI><strong>9th house</strong>: travel, higher education, philosophy, religion.</LI>
      <LI><strong>10th house</strong>: career, public reputation, ambition.</LI>
      <LI><strong>11th house</strong>: friends, groups, long-term goals.</LI>
      <LI><strong>12th house</strong>: solitude, the unconscious, the invisible.</LI>
    </UL>
    <P>
      A planet in a house tells you where that force plays out in life. Venus in the 10th house? Your love life often touches your public career. Mars in the 4th? You are combative at home in a way you may not be at work.
    </P>
    <P>
      You need your birth time to calculate houses. Without it, houses cannot be drawn.
    </P>

    <H2>The aspects are the {'"why the chart feels the way it feels"'}</H2>
    <P>
      Aspects are the angles between planets. Some angles are easy (planets support each other) and some are hard (planets pull against each other).
    </P>
    <UL>
      <LI><strong>Conjunction</strong> (0 degrees): fused, intense, blurred together.</LI>
      <LI><strong>Sextile</strong> (60 degrees): friendly, opportunity, unforced.</LI>
      <LI><strong>Square</strong> (90 degrees): tense, growth-inducing, motivating friction.</LI>
      <LI><strong>Trine</strong> (120 degrees): easy, flowing, sometimes lazy.</LI>
      <LI><strong>Opposition</strong> (180 degrees): pulled in two directions, needs balance.</LI>
    </UL>
    <P>
      Aspects are where a lot of real chart interpretation happens. A person with a lot of squares has a lot of internal tension and often a lot of drive. A person with many trines has an easier ride and sometimes has to invent challenges. Neither is better.
    </P>

    <H2>How to actually read a chart</H2>
    <P>
      Here is the order most working astrologers use. Try it on your own chart.
    </P>
    <UL>
      <LI>Start with the big three: sun, moon, rising. Read them together as a personality sketch.</LI>
      <LI>Look at where the ruling planet of the rising sign is placed. That planet colours the whole chart.</LI>
      <LI>Look for stelliums (three or more planets in one sign or house). Big energy concentrations. Life themes.</LI>
      <LI>Find Mars and Venus. They tell you a lot about how you love and how you fight.</LI>
      <LI>Find Saturn. It tells you where you struggle and where mastery becomes possible with time.</LI>
      <LI>Read the aspects, especially the tight ones (within a few degrees). Those are loud.</LI>
    </UL>

    <Quote>
      A chart is not a script. It is a language. What you say in it is still up to you.
    </Quote>

    <H2>Where to go from here</H2>
    <P>
      Once you can look at a chart and roughly say what is going on, everything else in astrology becomes more interesting. Transits, progressions, synastry, all of it builds on this same anatomy.
    </P>
    <P>
      If reading a full chart still feels like a lot, Astrarium does the interpretation for you. You give it your date, place, and time of birth, and it draws the almanac across seven chapters. Career, love, family, finances, health, the year ahead, and the strengths and shadows you actually live with. No wheel diagrams. Just the reading.
    </P>
  </LearnArticleLayout>
);
